/**
 * College Utility Functions
 * 
 * Provides utilities for working with college data, including slug-to-key mapping
 */

/**
 * Gets the slug from a college object
 * @param college - College object from collegiateFantasyData.colleges
 * @returns The slug if available, otherwise generates a slug from displayName
 */
export const getCollegeSlug = (college: any): string | null => {
  if (!college) return null;
  
  // Use slug if available
  if (college.slug) {
    return college.slug;
  }
  
  // Fallback: generate slug from displayName
  if (college.displayName) {
    return college.displayName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
  
  return null;
};

/**
 * Creates a mapping from slug to school key
 * @param colleges - The colleges object from collegiateFantasyData.colleges
 * @returns Map from slug to school key
 */
export const createSlugToKeyMap = (colleges: { [key: string]: any }): Map<string, string> => {
  const map = new Map<string, string>();
  
  if (!colleges) return map;
  
  Object.keys(colleges).forEach(key => {
    const college = colleges[key];
    const slug = getCollegeSlug(college);
    if (slug) {
      map.set(slug, key);
    }
  });
  
  return map;
};

/**
 * Creates a mapping from school key to slug
 * @param colleges - The colleges object from collegiateFantasyData.colleges
 * @returns Map from school key to slug
 */
export const createKeyToSlugMap = (colleges: { [key: string]: any }): Map<string, string> => {
  const map = new Map<string, string>();
  
  if (!colleges) return map;
  
  Object.keys(colleges).forEach(key => {
    const college = colleges[key];
    const slug = getCollegeSlug(college);
    if (slug) {
      map.set(key, slug);
    }
  });
  
  return map;
};

